import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import FormSelect from "@ats/src/components/forms/FormSelect";

interface MonthlyCreditUsage {
  month: string;
  creditsUsed: number;
}

const monthStart = (month: string) => {
  const [year, monthNumber] = month.split("-");
  return new Date(Number(year), Number(monthNumber) - 1, 1);
};

const monthLabel = (month: string) => {
  return monthStart(month).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const monthName = (month: string) => {
  return monthStart(month).toLocaleDateString("en-US", { month: "long" });
};

// "Aug 1 – 31" for a completed month, "Sep 1 – 9" for the current month (1st through today).
const dayRange = (month: string, isCurrentMonth: boolean) => {
  const start = monthStart(month);
  const monthAbbreviation = start.toLocaleDateString("en-US", { month: "short" });
  const endDay = isCurrentMonth
    ? new Date().getDate()
    : new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
  return `${monthAbbreviation} 1 – ${endDay}`;
};

const AiMonthlyCreditUsage: React.FC<{ monthlyCreditUsage: MonthlyCreditUsage[] }> = ({
  monthlyCreditUsage,
}) => {
  const [selectedMonth, setSelectedMonth] = React.useState("");

  const handleChangeMonth = (name: string, value: string) => {
    setSelectedMonth(value);
  };

  const lastIndex = monthlyCreditUsage.length - 1;
  const currentMonthUsage = monthlyCreditUsage[lastIndex];
  const selectedMonthValue =
    selectedMonth === "" && currentMonthUsage != undefined
      ? currentMonthUsage.month
      : selectedMonth;
  const selectedIndex = monthlyCreditUsage.findIndex(
    (monthlyUsage) => monthlyUsage.month === selectedMonthValue,
  );
  const selectedMonthUsage = monthlyCreditUsage[selectedIndex];
  const priorMonthUsage = monthlyCreditUsage[selectedIndex - 1];
  const isCurrentMonth = selectedIndex === lastIndex;

  const monthOptions = monthlyCreditUsage.map((monthlyUsage, index) => ({
    value: monthlyUsage.month,
    label: index === lastIndex ? `${monthLabel(monthlyUsage.month)} (current)` : monthLabel(monthlyUsage.month),
  }));

  const comparisonText = () => {
    if (priorMonthUsage == undefined) {
      return "No earlier month to compare";
    }
    const priorMonthName = monthName(priorMonthUsage.month);
    if (priorMonthUsage.creditsUsed === 0) {
      return selectedMonthUsage.creditsUsed === 0
        ? `Level with ${priorMonthName}`
        : `No usage in ${priorMonthName} to compare`;
    }
    const delta = Math.round(
      ((selectedMonthUsage.creditsUsed - priorMonthUsage.creditsUsed) / priorMonthUsage.creditsUsed) *
        100,
    );
    if (delta === 0) {
      return `Level with ${priorMonthName}`;
    }
    const arrow = delta > 0 ? "↑" : "↓";
    return `${arrow} ${Math.abs(delta)}% vs ${priorMonthName}${isCurrentMonth ? " so far" : ""}`;
  };

  if (selectedMonthUsage == undefined) {
    return null;
  }

  return (
    <>
      <Styled.MonthPicker>
        <FormSelect
          onChange={handleChangeMonth}
          name="month"
          value={selectedMonthValue}
          options={monthOptions}
        />
      </Styled.MonthPicker>

      <Styled.Comparison>
        <Styled.Cell>
          <Styled.CellHeading>
            <Styled.CellHeadingLeft>
              <Styled.Chip tone="prior" />
              <Styled.MonthName>
                {priorMonthUsage != undefined ? monthLabel(priorMonthUsage.month) : "Prior month"}
              </Styled.MonthName>
            </Styled.CellHeadingLeft>
            {priorMonthUsage != undefined ? (
              <Styled.DayRange>{dayRange(priorMonthUsage.month, false)}</Styled.DayRange>
            ) : null}
          </Styled.CellHeading>
          {priorMonthUsage != undefined ? (
            <>
              <Styled.Figure>
                {priorMonthUsage.creditsUsed.toLocaleString()} <span>credits used</span>
              </Styled.Figure>
              <Styled.Caption>Prior month</Styled.Caption>
            </>
          ) : (
            <Styled.Caption>
              {monthLabel(selectedMonthUsage.month)} is the earliest month with usage data.
            </Styled.Caption>
          )}
        </Styled.Cell>

        <Styled.Cell>
          <Styled.CellHeading>
            <Styled.CellHeadingLeft>
              <Styled.Chip tone="selected" />
              <Styled.MonthName>
                {isCurrentMonth ? "Current month" : monthLabel(selectedMonthUsage.month)}
              </Styled.MonthName>
            </Styled.CellHeadingLeft>
            <Styled.DayRange>{dayRange(selectedMonthUsage.month, isCurrentMonth)}</Styled.DayRange>
          </Styled.CellHeading>
          <Styled.Figure>
            {selectedMonthUsage.creditsUsed.toLocaleString()} <span>credits used</span>
          </Styled.Figure>
          {priorMonthUsage != undefined ? (
            <Styled.Delta>{comparisonText()}</Styled.Delta>
          ) : (
            <Styled.Caption>{comparisonText()}</Styled.Caption>
          )}
        </Styled.Cell>
      </Styled.Comparison>
    </>
  );
};

export default AiMonthlyCreditUsage;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

const toneColors = (t: any, tone: string) => {
  const map: any = {
    prior: t.dark ? t.color.gray[500] : t.color.gray[400],
    selected: t.dark ? t.color.gray[100] : t.color.black,
  };
  return map[tone];
};

Styled.MonthPicker = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_MonthPicker;
    max-width: 240px;
    ${[t.mb(4)]}
  `;
});

Styled.Comparison = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_Comparison;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    max-width: 696px;
    ${[t.rounded.md]}
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
  `;
});

Styled.Cell = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_Cell;
    min-width: 0;
    ${[t.p(5)]}

    & + & {
      border-left: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    }
  `;
});

Styled.CellHeading = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_CellHeading;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${t.spacing[3]};
  `;
});

Styled.CellHeadingLeft = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_CellHeadingLeft;
    display: flex;
    align-items: center;
    gap: ${t.spacing[3]};
    min-width: 0;
  `;
});

Styled.DayRange = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_DayRange;
    flex: 0 0 auto;
    ${[t.text.sm]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    font-variant-numeric: tabular-nums;
  `;
});

Styled.Chip = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_Chip;
    flex: 0 0 auto;
    width: 11px;
    height: 11px;
    ${[t.rounded.xs]}
    background: ${toneColors(t, props.tone)};
  `;
});

Styled.MonthName = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_MonthName;
    line-height: 1.1;
    ${[t.text.lg, t.text.medium]}
    color: ${t.dark ? t.color.gray[100] : t.color.gray[900]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `;
});

Styled.Figure = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_Figure;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0 ${t.spacing[2]};
    ${[t.mt(5), t.text.h2, t.text.bold]}
    color: ${t.dark ? t.color.gray[100] : t.color.black};
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
    line-height: 1.1;

    span {
      ${[t.text.sm]}
      font-weight: 400;
      letter-spacing: 0;
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
  `;
});

Styled.Caption = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_Caption;
    ${[t.mt(3), t.text.sm]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.Delta = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiMonthlyCreditUsage_Delta;
    ${[t.mt(3), t.text.sm, t.text.medium]}
    color: ${t.dark ? t.color.gray[200] : t.color.gray[900]};
    font-variant-numeric: tabular-nums;
  `;
});
